import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';

/**
 * DateFilter - Reusable date filter for clinic dashboard views
 *
 * Modes:
 * - 'monthYear': month + year selector (converts to start_date/end_date for API)
 * - 'date': single date input (for DailyOperations)
 * - 'year': year grid selector (for year-based views)
 */
const DateFilter = ({
    mode = 'year',
    value,
    onChange,
    defaultYear = new Date().getFullYear()
}) => {
    const [isOpen, setIsOpen] = useState(false);
    // Internal browse year for monthYear mode — only used while dropdown is open
    const [browseYear, setBrowseYear] = useState(null);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    const selectedMonth = value?.month ?? currentMonth;
    const selectedYear = value?.year ?? currentYear;

    // Sync browseYear when dropdown opens
    useEffect(() => {
        if (isOpen) {
            setBrowseYear(selectedYear);
        }
    }, [isOpen, selectedYear]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleYearSelect = useCallback((year) => {
        onChange({ year });
        setIsOpen(false);
    }, [onChange]);

    const handleDateChange = useCallback((dateVal) => {
        onChange({ date: dateVal });
    }, [onChange]);

    const handleMonthSelect = useCallback((month, year) => {
        // Calculate start and end dates for the selected month
        const startDate = new Date(year, month, 1);
        const endDate = new Date(year, month + 1, 0);

        const formatDate = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        onChange({
            month,
            year,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate)
        });
        setIsOpen(false);
    }, [onChange]);

    const getDisplayLabel = () => {
        if (mode === 'year') {
            return value?.year ? `Year ${value.year}` : `Year ${defaultYear}`;
        }
        if (mode === 'monthYear') {
            const m = value?.month ?? currentMonth;
            const y = value?.year ?? currentYear;
            const monthName = new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long' });
            return `${monthName} ${y}`;
        }
        if (mode === 'date') {
            if (!value?.date) return 'Today';
            const d = new Date(value.date);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
        return '';
    };

    const isActive = () => {
        if (mode === 'year') return !!value?.year && value.year !== defaultYear;
        if (mode === 'monthYear') {
            const defaultMonth = currentMonth;
            const defaultYearVal = currentYear;
            return (value?.month !== undefined && value.month !== defaultMonth) ||
                   (value?.year !== undefined && value.year !== defaultYearVal);
        }
        if (mode === 'date') return !!value?.date;
        return false;
    };

    const clearFilter = (e) => {
        e.stopPropagation();
        if (mode === 'year') {
            onChange({ year: defaultYear });
        } else if (mode === 'monthYear') {
            handleMonthSelect(currentMonth, currentYear);
        } else if (mode === 'date') {
            onChange({ date: '' });
        }
    };

    const stopPropagation = (e) => {
        e.stopPropagation();
    };

    // Generate month options
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const displayYear = browseYear ?? selectedYear;

    // Generate year grid: show a 3x3 grid centered around selected/browse year
    const generateYearGrid = () => {
        const center = displayYear;
        const start = center - 4;
        const years = [];
        for (let i = 0; i < 9; i++) {
            years.push(start + i);
        }
        return years;
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }} onClick={stopPropagation}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="clinic-dashboard-datefilter-trigger"
                data-active={isActive()}
            >
                <Calendar size={14} />
                <span>{getDisplayLabel()}</span>
                {isActive() ? (
                    <X size={14} onClick={clearFilter} className="clinic-dashboard-datefilter-clear" />
                ) : (
                    <ChevronDown size={14} className={`clinic-dashboard-datefilter-chevron ${isOpen ? 'open' : ''}`} />
                )}
            </button>

            {isOpen && (
                <div
                    ref={dropdownRef}
                    className="clinic-dashboard-datefilter-dropdown"
                    onClick={stopPropagation}
                >
                    {mode === 'year' && (
                        <div className="clinic-dashboard-datefilter-year-panel">
                            {/* Year navigation */}
                            <div className="clinic-dashboard-datefilter-year" style={{ marginBottom: '12px' }}>
                                <button
                                    type="button"
                                    className="clinic-dashboard-datefilter-year-btn"
                                    onClick={() => setBrowseYear(displayYear - 9)}
                                >
                                    ‹
                                </button>
                                <span className="clinic-dashboard-datefilter-year-value">
                                    {displayYear}
                                </span>
                                <button
                                    type="button"
                                    className="clinic-dashboard-datefilter-year-btn"
                                    onClick={() => setBrowseYear(displayYear + 9)}
                                    disabled={displayYear + 9 > currentYear + 1}
                                >
                                    ›
                                </button>
                            </div>

                            {/* Year grid */}
                            <div className="clinic-dashboard-datefilter-year-grid">
                                {generateYearGrid().map((year) => {
                                    const isFuture = year > currentYear;
                                    const isSelected = year === selectedYear;
                                    return (
                                        <button
                                            key={year}
                                            type="button"
                                            className={`clinic-dashboard-datefilter-year-block ${isSelected ? 'selected' : ''} ${isFuture ? 'disabled' : ''}`}
                                            onClick={() => {
                                                if (!isFuture) {
                                                    handleYearSelect(year);
                                                }
                                            }}
                                            disabled={isFuture}
                                        >
                                            {year}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {mode === 'monthYear' && (
                        <div className="clinic-dashboard-datefilter-monthyear">
                            {/* Year selector — pure navigation, does NOT trigger API */}
                            <div className="clinic-dashboard-datefilter-year" style={{ marginBottom: '12px' }}>
                                <button
                                    type="button"
                                    className="clinic-dashboard-datefilter-year-btn"
                                    onClick={() => setBrowseYear(displayYear - 1)}
                                >
                                    ‹
                                </button>
                                <span className="clinic-dashboard-datefilter-year-value">
                                    {displayYear}
                                </span>
                                <button
                                    type="button"
                                    className="clinic-dashboard-datefilter-year-btn"
                                    onClick={() => setBrowseYear(displayYear + 1)}
                                    disabled={displayYear >= currentYear}
                                >
                                    ›
                                </button>
                            </div>

                            {/* Month grid — only this triggers onChange */}
                            <div className="clinic-dashboard-datefilter-month-grid">
                                {monthNames.map((name, idx) => {
                                    const isFuture = displayYear > currentYear || (displayYear === currentYear && idx > currentMonth);
                                    const isSelected = idx === selectedMonth && displayYear === selectedYear;
                                    return (
                                        <button
                                            key={name}
                                            type="button"
                                            className={`clinic-dashboard-datefilter-month-btn ${isSelected ? 'selected' : ''} ${isFuture ? 'disabled' : ''}`}
                                            onClick={() => {
                                                if (!isFuture) {
                                                    handleMonthSelect(idx, displayYear);
                                                }
                                            }}
                                            disabled={isFuture}
                                        >
                                            {name.slice(0, 3)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {mode === 'date' && (
                        <DatePicker
                            value={value?.date || ''}
                            onChange={(date) => {
                                handleDateChange(date);
                                setIsOpen(false);
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const DatePicker = ({ value, onChange }) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    const [pickYear, setPickYear] = useState(currentYear);
    const [pickMonth, setPickMonth] = useState(currentMonth);
    const [pickDay, setPickDay] = useState(currentDay);
    const [showMonthList, setShowMonthList] = useState(false);
    const [showYearList, setShowYearList] = useState(false);

    // Parse initial value if exists
    useEffect(() => {
        if (value) {
            const d = new Date(value);
            setPickYear(d.getFullYear());
            setPickMonth(d.getMonth());
            setPickDay(d.getDate());
        }
    }, [value]);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const daysInMonth = new Date(pickYear, pickMonth + 1, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push(y);
    }

    const isFuture = (y, m, d) => {
        if (y > currentYear) return true;
        if (y === currentYear && m > currentMonth) return true;
        if (y === currentYear && m === currentMonth && d > currentDay) return true;
        return false;
    };

    const handleSelect = (y, m, d) => {
        if (isFuture(y, m, d)) return;
        const formatted = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        onChange(formatted);
    };

    return (
        <div className="clinic-dashboard-datefilter-datepicker">
            <div className="clinic-dashboard-datefilter-datepicker-header">
                <span>Select date</span>
                <button
                    type="button"
                    className="clinic-dashboard-datefilter-today-btn"
                    onClick={() => {
                        const today = new Date();
                        handleSelect(today.getFullYear(), today.getMonth(), today.getDate());
                    }}
                >
                    Today
                </button>
            </div>

            <div className="clinic-dashboard-datefilter-datepicker-fields">
                <div className="clinic-dashboard-datefilter-datepicker-field">
                    <label>Day</label>
                    <div className="clinic-dashboard-datefilter-day-grid">
                        {days.map((d) => {
                            const future = isFuture(pickYear, pickMonth, d);
                            const selected = d === pickDay;
                            return (
                                <button
                                    key={d}
                                    type="button"
                                    className={`clinic-dashboard-datefilter-day-btn ${selected ? 'selected' : ''} ${future ? 'disabled' : ''}`}
                                    onClick={() => {
                                        setPickDay(d);
                                        handleSelect(pickYear, pickMonth, d);
                                    }}
                                    disabled={future}
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="clinic-dashboard-datefilter-datepicker-col">
                    {/* Custom Month Dropdown */}
                    <div className="clinic-dashboard-datefilter-datepicker-field" style={{ position: 'relative' }}>
                        <label>Month</label>
                        <button
                            type="button"
                            className="clinic-dashboard-datefilter-custom-select"
                            onClick={() => {
                                setShowMonthList(!showMonthList);
                                setShowYearList(false);
                            }}
                        >
                            {monthNames[pickMonth]}
                            <ChevronDown size={14} className={showMonthList ? 'open' : ''} />
                        </button>
                        {showMonthList && (
                            <div className="clinic-dashboard-datefilter-custom-select-list">
                                {monthNames.map((name, idx) => {
                                    const future = isFuture(pickYear, idx, 1);
                                    return (
                                        <button
                                            key={name}
                                            type="button"
                                            className={`clinic-dashboard-datefilter-custom-select-item ${idx === pickMonth ? 'selected' : ''} ${future ? 'disabled' : ''}`}
                                            onClick={() => {
                                                if (!future) {
                                                    setPickMonth(idx);
                                                    const maxDay = new Date(pickYear, idx + 1, 0).getDate();
                                                    setPickDay(prev => Math.min(prev, maxDay));
                                                    setShowMonthList(false);
                                                }
                                            }}
                                            disabled={future}
                                        >
                                            {name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Custom Year Dropdown */}
                    <div className="clinic-dashboard-datefilter-datepicker-field" style={{ position: 'relative' }}>
                        <label>Year</label>
                        <button
                            type="button"
                            className="clinic-dashboard-datefilter-custom-select"
                            onClick={() => {
                                setShowYearList(!showYearList);
                                setShowMonthList(false);
                            }}
                        >
                            {pickYear}
                            <ChevronDown size={14} className={showYearList ? 'open' : ''} />
                        </button>
                        {showYearList && (
                            <div className="clinic-dashboard-datefilter-custom-select-list">
                                {years.map((y) => (
                                    <button
                                        key={y}
                                        type="button"
                                        className={`clinic-dashboard-datefilter-custom-select-item ${y === pickYear ? 'selected' : ''}`}
                                        onClick={() => {
                                            setPickYear(y);
                                            setShowYearList(false);
                                        }}
                                    >
                                        {y}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DateFilter;
